import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import Keycloak from 'keycloak-js'
import { injectInfiniteQuery, injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental'
import { lastValueFrom } from 'rxjs'
import { ButtonModule } from '@openng/optimus-ui/button'
import { DialogModule } from '@openng/optimus-ui/dialog'
import { Textarea } from '@openng/optimus-ui/textarea'
import { Skeleton } from '@openng/optimus-ui/skeleton'
import { ToastModule } from '@openng/optimus-ui/toast'
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog'
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api'

import {
	MarketplaceReview,
	MarketplaceReviewService,
	MarketplaceReviewSummary,
} from '../../core/marketplace-review.service'
import { SecureTransactionService } from '../../core/secure-transaction.service'
import { faDate, faNum } from '../../features/public/equipment-page/equipment-labels'

const PAGE_SIZE = 5

/**
 * Marketplace M7 — reviews block for one seller (PRD ch.20):
 * summary (avg + satisfaction or the «داده کافی وجود ندارد» notice), the paged
 * public list with seller replies and the transaction-scoped actions:
 *  - «ثبت نظر»: only the SETTLED buyer of `adId()` (detected via my settled
 *    BUYER transactions — one review per tx, enforced by the backend too).
 *  - edit/delete of my own review (buyer).
 *  - reply/report on each row when the viewer IS the seller (equipment page of
 *    my own ad, or the seller viewing their own /sellers/:id profile).
 *
 * Guests trigger only the public GETs — every mutation sits behind buttons
 * that require authentication, and the server enforces the roles anyway.
 */
@Component({
	selector: 'app-marketplace-reviews-section',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RouterLink,
		ButtonModule,
		DialogModule,
		Textarea,
		Skeleton,
		ToastModule,
		ConfirmDialog,
	],
	providers: [MessageService, ConfirmationService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<section class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm" id="reviews">
			<!-- Header: title + summary -->
			<div class="flex items-center justify-between flex-wrap gap-3 mb-1">
				<h2 class="text-base font-black text-slate-800 flex items-center gap-2">
					<i class="pi pi-comments text-purple-500"></i> نظرات
					<span class="text-[11px] font-bold text-slate-400">خریداران تأییدشده پرداخت امن</span>
				</h2>
				@if (showProfileLink()) {
					<a
						[routerLink]="['/sellers', sellerKcUserId()]"
						class="text-xs font-bold text-purple-600 hover:text-purple-700 inline-flex items-center gap-1">
						پروفایل فروشنده <i class="pi pi-arrow-left text-[10px]"></i>
					</a>
				}
			</div>

			@if (summaryQuery.isPending()) {
				<p-skeleton height="2rem" width="60%" styleClass="rounded-xl mb-4" />
			} @else if (summary(); as s) {
				<div class="flex items-center gap-3 flex-wrap text-xs mb-4">
					<span class="inline-flex items-center gap-1.5" dir="ltr">
						<span class="text-amber-400">{{ stars(s.avgSellerRating) }}</span>
						<span class="text-slate-200">{{ stars(5 - Math.round(s.avgSellerRating ?? 0)) }}</span>
					</span>
					<b class="text-slate-700 text-sm">{{ faNum(s.avgSellerRating) }}</b>
					<span class="text-slate-400">از {{ (s.sellerCount ?? 0).toLocaleString('fa-IR') }} نظر خریدار</span>
					@if (showSatisfaction()) {
						<span
							class="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 font-bold px-2.5 py-1 rounded-lg">
							<i class="pi pi-face-smile"></i> رضایت {{ faNum(summary()!.satisfactionPercent, 0) }}٪ کاربران
						</span>
					} @else {
						<span class="inline-flex items-center gap-1 bg-slate-50 text-slate-400 font-bold px-2.5 py-1 rounded-lg">
							<i class="pi pi-info-circle"></i> داده کافی وجود ندارد
						</span>
					}
				</div>
			}

			<!-- Action strip: my review / submit CTA / hints -->
			@if (myReview(); as mine) {
				<div
					class="rounded-2xl border border-purple-100 bg-purple-50/50 p-4 mb-5 flex items-center justify-between flex-wrap gap-2">
					<p class="text-xs font-bold text-purple-700">
						<i class="pi pi-check-circle ml-1"></i> نظر شما برای این معامله ثبت شده
						@if (mine.edited) {
							<span class="text-[10px] text-slate-400 font-normal">(ویرایش‌شده)</span>
						}
					</p>
					<div class="flex gap-2">
						<button [text]="true" (click)="openEditDialog(mine)" pButton size="small" label="ویرایش"></button>
						<button [text]="true" (click)="askDelete()" pButton size="small" severity="danger" label="حذف"></button>
					</div>
				</div>
			} @else if (canReview()) {
				<button (click)="openCreateDialog()" pButton class="w-full mb-5">
					<i class="pi pi-star ml-1"></i> ثبت نظر برای این خرید
				</button>
			} @else if (isAuthed() && adId()) {
				<p class="text-[11px] text-slate-400 bg-slate-50 rounded-xl px-3 py-2 mb-5">
					ثبت نظر تنها برای خریدارِ معامله‌های تسویه‌شده همین آگهی امکان‌پذیر است.
				</p>
			}

			<!-- Public list -->
			@if (listQuery.isPending() && !rows().length) {
				@for (i of [1, 2, 3]; track i) {
					<p-skeleton height="5.5rem" styleClass="rounded-2xl mb-3" />
				}
			} @else if (!rows().length) {
				<p class="text-sm text-slate-400 py-6 text-center">هنوز نظری برای این فروشنده ثبت نشده است.</p>
			} @else {
				<div class="space-y-3">
					@for (review of rows(); track review.id) {
						<article [class.opacity-60]="review.hidden" class="rounded-2xl border border-slate-100 p-4">
							<div class="flex items-start justify-between gap-3 flex-wrap mb-2">
								<div class="flex items-center gap-2.5">
									<div class="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
										<i class="pi pi-check text-emerald-500 text-xs"></i>
									</div>
									<div>
										<p class="text-xs font-bold text-slate-700">خریدار تأییدشده</p>
										<p class="text-[10px] text-slate-400">{{ faDate(review.createdDate) }}</p>
									</div>
								</div>
								<div class="flex items-center gap-2 text-sm">
									<span class="inline-flex items-center gap-1" title="امتیاز فروشنده">
										<span dir="ltr" class="text-amber-400">{{ stars(review.sellerRating) }}</span>
										<span dir="ltr" class="text-slate-200">{{ stars(5 - (review.sellerRating ?? 0)) }}</span>
									</span>
									@if (review.itemRating) {
										<span
											class="inline-flex items-center gap-1 text-[11px] text-slate-500 border border-slate-100 rounded-lg px-2 py-0.5"
											title="امتیاز کالا">
											<i class="pi pi-box text-purple-400"></i>
											<span dir="ltr" class="text-amber-400">{{ stars(review.itemRating) }}</span>
											<span dir="ltr" class="text-slate-200">{{ stars(5 - review.itemRating) }}</span>
										</span>
									}
								</div>
							</div>

							@if (review.comment) {
								<p class="text-sm text-slate-600 leading-7 whitespace-pre-line">{{ review.comment }}</p>
							}
							@if (review.edited) {
								<p class="text-[10px] text-slate-300 mt-1">ویرایش‌شده</p>
							}

							@if (review.reply) {
								<div class="mt-3 rounded-xl bg-slate-50 border-r-2 border-purple-300 pr-3 py-2">
									<p class="text-[10px] font-black text-purple-500 mb-0.5">
										پاسخ فروشنده
										@if (review.repliedAt) {
											<span class="font-normal text-slate-400">— {{ faDate(review.repliedAt) }}</span>
										}
									</p>
									<p class="text-xs text-slate-600 leading-6 whitespace-pre-line">{{ review.reply }}</p>
								</div>
							}

							<!-- Seller actions (viewer is this seller) -->
							@if (viewerIsSeller() && !review.hidden) {
								<div class="flex gap-2 mt-3">
									<button [text]="true" (click)="openReplyDialog(review)" pButton size="small" class="!text-[11px]">
										<i class="pi pi-reply ml-1"></i> {{ review.reply ? 'ویرایش پاسخ' : 'پاسخ' }}
									</button>
									<button
										[text]="true"
										(click)="openReportDialog(review)"
										pButton
										size="small"
										class="!text-[11px] !text-red-400">
										<i class="pi pi-flag ml-1"></i> گزارش نظر
									</button>
								</div>
							}
						</article>
					}
				</div>

				@if (listQuery.hasNextPage()) {
					<div class="text-center mt-4">
						<button
							[outlined]="true"
							[loading]="listQuery.isFetchingNextPage()"
							(click)="loadMore()"
							pButton
							size="small"
							label="نمایش نظرات بیشتر"></button>
					</div>
				}
			}
		</section>

		<!-- ── Submit / edit dialog (buyer) ─────────────────────────────────── -->
		<p-dialog
			[visible]="reviewDialogOpen()"
			[modal]="true"
			[header]="editingReview() ? 'ویرایش نظر' : 'ثبت نظر برای این خرید'"
			[style]="{ width: 'min(30rem, 92vw)' }"
			[closable]="true"
			(visibleChange)="reviewDialogOpen.set($event)">
			<div class="space-y-4">
				<div>
					<label class="block text-xs font-bold text-slate-600 mb-1.5"
						>امتیاز فروشنده <span class="text-red-500">*</span></label
					>
					<div class="flex items-center gap-2">
						<div class="flex items-center gap-1" dir="ltr">
							@for (star of [1, 2, 3, 4, 5]; track star) {
								<button
									[attr.aria-label]="star + ' ستاره'"
									[class]="draftSellerRating() >= star ? 'text-amber-400' : 'text-slate-200'"
									(click)="draftSellerRating.set(star)"
									type="button"
									class="text-2xl leading-none transition-transform hover:scale-110"
									>★</button
								>
							}
						</div>
						<span class="text-xs text-slate-400">{{
							draftSellerRating() ? faNum(draftSellerRating(), 0) + ' از ۵' : 'انتخاب کنید'
						}}</span>
					</div>
				</div>
				<div>
					<label class="block text-xs font-bold text-slate-600 mb-1.5"
						>امتیاز کالا <span class="text-[10px] font-normal text-slate-400">(اختیاری)</span></label
					>
					<div class="flex items-center gap-2">
						<div class="flex items-center gap-1" dir="ltr">
							@for (star of [1, 2, 3, 4, 5]; track star) {
								<button
									[attr.aria-label]="star + ' ستاره'"
									[class]="draftItemRating() >= star ? 'text-amber-400' : 'text-slate-200'"
									(click)="draftItemRating.set(draftItemRating() === star ? 0 : star)"
									type="button"
									class="text-2xl leading-none transition-transform hover:scale-110"
									>★</button
								>
							}
						</div>
						<span class="text-xs text-slate-400">{{
							draftItemRating() ? faNum(draftItemRating(), 0) + ' از ۵' : 'بدون امتیاز'
						}}</span>
					</div>
				</div>
				<div>
					<label class="block text-xs font-bold text-slate-600 mb-1.5"
						>تجربه شما <span class="text-[10px] font-normal text-slate-400">(اختیاری)</span></label
					>
					<textarea
						[rows]="4"
						[ngModel]="draftComment()"
						(ngModelChange)="draftComment.set($event)"
						pTextarea
						class="w-full"
						placeholder="کیفیت کالا، تعامل با فروشنده، روند ارسال…"></textarea>
				</div>
				<p class="text-[11px] text-slate-400 leading-5"
					>نظر شما به‌عنوان «خریدار تأییدشده» منتشر می‌شود؛ فروشنده می‌تواند به آن پاسخ دهد.</p
				>
				<div class="flex justify-end gap-2">
					<button [text]="true" (click)="reviewDialogOpen.set(false)" pButton label="انصراف"></button>
					<button
						[label]="editingReview() ? 'ذخیره تغییرات' : 'ثبت نظر'"
						[disabled]="!draftSellerRating()"
						[loading]="saveMutation.isPending()"
						(click)="saveReview()"
						pButton></button>
				</div>
			</div>
		</p-dialog>

		<!-- ── Reply dialog (seller) ────────────────────────────────────────── -->
		<p-dialog
			[visible]="replyDialogOpen()"
			[modal]="true"
			[style]="{ width: 'min(28rem, 92vw)' }"
			[closable]="true"
			(visibleChange)="replyDialogOpen.set($event)"
			header="پاسخ به نظر">
			<div class="space-y-3">
				@if (replyTarget(); as target) {
					<p class="text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2 leading-6">{{ target.comment || '—' }}</p>
				}
				<textarea
					[rows]="3"
					[ngModel]="draftReply()"
					(ngModelChange)="draftReply.set($event)"
					pTextarea
					class="w-full"
					placeholder="پاسخ شما به این نظر…"></textarea>
				<div class="flex justify-end gap-2">
					<button [text]="true" (click)="replyDialogOpen.set(false)" pButton label="انصراف"></button>
					<button
						[disabled]="!draftReply().trim()"
						[loading]="replyMutation.isPending()"
						(click)="submitReply()"
						pButton
						label="ثبت پاسخ"></button>
				</div>
			</div>
		</p-dialog>

		<!-- ── Report dialog (seller) ───────────────────────────────────────── -->
		<p-dialog
			[visible]="reportDialogOpen()"
			[modal]="true"
			[style]="{ width: 'min(28rem, 92vw)' }"
			[closable]="true"
			(visibleChange)="reportDialogOpen.set($event)"
			header="گزارش نظر نامعتبر">
			<div class="space-y-3">
				<p class="text-xs text-slate-500 leading-6"
					>دلیل گزارش را بنویسید (مثلاً توهین، تبلیغ یا بی‌ارتباطی با معامله). کارشناسان بیوتی‌باس بررسی می‌کنند.</p
				>
				<textarea
					[rows]="3"
					[ngModel]="draftReportReason()"
					(ngModelChange)="draftReportReason.set($event)"
					pTextarea
					class="w-full"
					placeholder="دلیل گزارش…"></textarea>
				<div class="flex justify-end gap-2">
					<button [text]="true" (click)="reportDialogOpen.set(false)" pButton label="انصراف"></button>
					<button
						[disabled]="draftReportReason().trim().length < 3"
						[loading]="reportMutation.isPending()"
						(click)="submitReport()"
						pButton
						severity="danger"
						label="ارسال گزارش"></button>
				</div>
			</div>
		</p-dialog>

		<p-confirmdialog />
	`,
})
export class MarketplaceReviewsSection {
	private readonly api = inject(MarketplaceReviewService)
	private readonly txService = inject(SecureTransactionService)
	private readonly queryClient = inject(QueryClient)
	private readonly toast = inject(MessageService)
	private readonly confirmation = inject(ConfirmationService)
	private readonly keycloak = inject(Keycloak)

	/** Whose reviews — required. */
	readonly sellerKcUserId = input.required<string>()
	/** When set, enables the SETTLED-buyer submit/edit flow for this ad. */
	readonly adId = input<string | null>(null)
	/** Hide the «پروفایل فروشنده» header link (the seller page itself uses this block). */
	readonly showProfileLink = input(true)

	readonly Math = Math
	readonly faDate = faDate
	readonly faNum = faNum

	/* ── Viewer context ───────────────────────────────────────────────── */

	isAuthed(): boolean {
		return this.keycloak.authenticated === true
	}

	private mySub(): string | null {
		return (this.keycloak.tokenParsed as { sub?: string } | undefined)?.sub ?? null
	}

	readonly viewerIsSeller = computed(() => {
		const sub = this.mySub()
		return !!sub && sub === this.sellerKcUserId()
	})

	/* ── Summary (public) ─────────────────────────────────────────────── */

	readonly summaryQuery = injectQuery(() => ({
		queryKey: ['mp-review-summary', this.sellerKcUserId()],
		queryFn: () => lastValueFrom(this.api.sellerSummary(this.sellerKcUserId())),
	}))

	readonly summary = computed<MarketplaceReviewSummary | null>(() => this.summaryQuery.data() ?? null)

	/** Chip only with real data — enoughData AND a non-null percent (TS-side null check). */
	readonly showSatisfaction = computed(() => {
		const s = this.summary()
		return !!s?.enoughData && s.satisfactionPercent != null
	})

	/* ── Paged public list («بیشتر» appends) ──────────────────────────── */

	readonly listQuery = injectInfiniteQuery(() => ({
		queryKey: ['mp-seller-reviews', this.sellerKcUserId()],
		initialPageParam: 0,
		queryFn: ({ pageParam }: { pageParam: number }) =>
			lastValueFrom(this.api.listForSeller(this.sellerKcUserId(), pageParam, PAGE_SIZE)),
		getNextPageParam: (last: MarketplaceReviewPageLike, all: MarketplaceReviewPageLike[]) => {
			const loaded = all.reduce((n, p) => n + (p.content?.length ?? 0), 0)
			return (last.totalElements ?? 0) > loaded ? all.length : undefined
		},
	}))

	/** Hidden rows stay out of the public list (admin-only view). */
	readonly rows = computed<MarketplaceReview[]>(() =>
		(this.listQuery.data()?.pages ?? []).flatMap(p => p.content ?? []).filter(r => !r.hidden)
	)

	loadMore() {
		void this.listQuery.fetchNextPage()
	}

	stars(value?: number | null): string {
		return '★'.repeat(Math.max(0, Math.min(5, Math.round(value ?? 0))))
	}

	/* ── My SETTLED buyer tx on this ad (submit permission) ───────────── */

	private readonly mySettledTxQuery = injectQuery(() => ({
		queryKey: ['mp-my-settled-buyer-tx', this.adId()],
		enabled: !!this.adId() && this.isAuthed(),
		queryFn: () => lastValueFrom(this.txService.getMy({ side: 'BUYER', status: 'SETTLED', page: 0, size: 50 })),
	}))

	/** First SETTLED buyer tx of mine on this ad — the review target. */
	private readonly mySettledTx = computed(
		() => (this.mySettledTxQuery.data()?.content ?? []).find(t => t.adId === this.adId()) ?? null
	)

	/** My own review — matched by buyer id or by my settled tx ids. */
	readonly myReview = computed<MarketplaceReview | null>(() => {
		const sub = this.mySub()
		const myTxIds = new Set(
			(this.mySettledTxQuery.data()?.content ?? [])
				.filter(t => t.adId === this.adId())
				.map(t => t.id)
				.filter((id): id is string => !!id)
		)
		return (
			this.rows().find(r => (sub && r.buyerKcUserId && r.buyerKcUserId === sub) || (r.txId && myTxIds.has(r.txId))) ??
			null
		)
	})

	readonly canReview = computed(() => !!this.adId() && !!this.mySettledTx() && !this.myReview())

	/* ── Submit / edit dialog ─────────────────────────────────────────── */

	readonly reviewDialogOpen = signal(false)
	readonly editingReview = signal<MarketplaceReview | null>(null)
	readonly draftSellerRating = signal(0)
	readonly draftItemRating = signal(0)
	readonly draftComment = signal('')

	openCreateDialog() {
		this.editingReview.set(null)
		this.draftSellerRating.set(0)
		this.draftItemRating.set(0)
		this.draftComment.set('')
		this.reviewDialogOpen.set(true)
	}

	openEditDialog(review: MarketplaceReview) {
		this.editingReview.set(review)
		this.draftSellerRating.set(review.sellerRating ?? 0)
		this.draftItemRating.set(review.itemRating ?? 0)
		this.draftComment.set(review.comment ?? '')
		this.reviewDialogOpen.set(true)
	}

	readonly saveMutation = injectMutation(() => ({
		mutationFn: () => {
			const payload = {
				sellerRating: this.draftSellerRating(),
				itemRating: this.draftItemRating() || null,
				comment: this.draftComment().trim() || null,
			}
			const editing = this.editingReview()
			if (editing?.id) return lastValueFrom(this.api.update(editing.id, payload))
			const txId = this.mySettledTx()?.id
			if (!txId) throw new Error('missing tx')
			return lastValueFrom(this.api.submitForTx(txId, payload))
		},
		onSuccess: () => {
			this.toast.add({
				severity: 'success',
				summary: this.editingReview() ? 'نظر شما به‌روزرسانی شد' : 'نظر شما ثبت شد',
			})
			this.reviewDialogOpen.set(false)
			this.invalidate()
		},
		onError: (err: unknown) => this.showError(err, 'ثبت نظر ناموفق بود'),
	}))

	saveReview() {
		if (!this.draftSellerRating()) return
		this.saveMutation.mutate()
	}

	askDelete() {
		this.confirmation.confirm({
			message: 'نظر خود را حذف می‌کنید؟ این عمل قابل بازگشت نیست.',
			icon: 'pi pi-exclamation-circle',
			acceptLabel: 'بله، حذف شود',
			rejectLabel: 'انصراف',
			accept: () => this.deleteMutation.mutate(),
		})
	}

	readonly deleteMutation = injectMutation(() => ({
		mutationFn: () => {
			const id = this.myReview()?.id
			if (!id) throw new Error('missing review')
			return lastValueFrom(this.api.remove(id))
		},
		onSuccess: () => {
			this.toast.add({ severity: 'success', summary: 'نظر شما حذف شد' })
			this.invalidate()
		},
		onError: (err: unknown) => this.showError(err, 'حذف نظر ناموفق بود'),
	}))

	/* ── Reply (seller) ───────────────────────────────────────────────── */

	readonly replyDialogOpen = signal(false)
	readonly replyTarget = signal<MarketplaceReview | null>(null)
	readonly draftReply = signal('')

	openReplyDialog(review: MarketplaceReview) {
		this.replyTarget.set(review)
		this.draftReply.set(review.reply ?? '')
		this.replyDialogOpen.set(true)
	}

	readonly replyMutation = injectMutation(() => ({
		mutationFn: () => {
			const id = this.replyTarget()?.id
			if (!id) throw new Error('missing review')
			return lastValueFrom(this.api.reply(id, this.draftReply().trim()))
		},
		onSuccess: () => {
			this.toast.add({ severity: 'success', summary: 'پاسخ شما ثبت شد' })
			this.replyDialogOpen.set(false)
			this.invalidate()
		},
		onError: (err: unknown) => this.showError(err, 'ثبت پاسخ ناموفق بود'),
	}))

	submitReply() {
		if (!this.draftReply().trim()) return
		this.replyMutation.mutate()
	}

	/* ── Report (seller) ──────────────────────────────────────────────── */

	readonly reportDialogOpen = signal(false)
	readonly reportTarget = signal<MarketplaceReview | null>(null)
	readonly draftReportReason = signal('')

	openReportDialog(review: MarketplaceReview) {
		this.reportTarget.set(review)
		this.draftReportReason.set('')
		this.reportDialogOpen.set(true)
	}

	readonly reportMutation = injectMutation(() => ({
		mutationFn: () => {
			const id = this.reportTarget()?.id
			if (!id) throw new Error('missing review')
			return lastValueFrom(this.api.report(id, this.draftReportReason().trim()))
		},
		onSuccess: () => {
			this.toast.add({ severity: 'success', summary: 'گزارش شما ثبت شد', detail: 'کارشناسان بیوتی‌باس بررسی می‌کنند.' })
			this.reportDialogOpen.set(false)
		},
		onError: (err: unknown) => this.showError(err, 'ارسال گزارش ناموفق بود'),
	}))

	submitReport() {
		if (this.draftReportReason().trim().length < 3) return
		this.reportMutation.mutate()
	}

	/* ── Shared ───────────────────────────────────────────────────────── */

	private invalidate() {
		void this.queryClient.invalidateQueries({ queryKey: ['mp-seller-reviews', this.sellerKcUserId()] })
		void this.queryClient.invalidateQueries({ queryKey: ['mp-review-summary', this.sellerKcUserId()] })
	}

	private showError(err: unknown, fallback: string) {
		const detail = (err as { error?: { message?: string } })?.error?.message ?? fallback
		this.toast.add({ severity: 'error', summary: 'خطا', detail })
	}
}

/** Minimal structural type — keeps the bridge import surface small. */
interface MarketplaceReviewPageLike {
	content?: (MarketplaceReview | undefined)[] | null
	totalElements?: number
}
